import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Select, SelectOption } from 'vopay';
import useDateLocale from '@/hooks/useDateLocale';
import { RecurrenceEndCondition, RecurrencePreset, RecurrenceRule } from './recurrence.types';
import { getOrdinalWeekdayCode, ordinalLabel } from './recurrence.utils';

export type RecurrencePresetSelectProps = {
  value: RecurrenceRule;
  onChange: (rule: RecurrenceRule) => void;
  startDate: Date | undefined;
  // Only consulted to decide whether selecting Custom should default an end condition — the
  // Recurring Payment card (not this dropdown) is what actually displays/edits Ends now.
  ends?: RecurrenceEndCondition;
  onEndsChange?: (ends: RecurrenceEndCondition) => void;
  name?: string;
  disabled?: boolean;
};

// Recurrence preset dropdown. Selecting "Custom…" just sets sensible defaults; the caller
// renders RecurringPaymentCard whenever value.preset !== 'one-time'.
export default function RecurrencePresetSelect({
  value,
  onChange,
  startDate,
  ends,
  onEndsChange,
  name,
  disabled,
}: RecurrencePresetSelectProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'UI.RecurrencePresetSelect' });
  const dateLocale = useDateLocale();

  function presetOptions(): SelectOption<{ label: string; value: RecurrencePreset }>[] {
    const oneTimeOption: { label: string; value: RecurrencePreset } = {
      label: t('Options.oneTime', { defaultValue: 'One time only' }),
      value: 'one-time',
    };
    const customOption: { label: string; value: RecurrencePreset } = {
      label: t('Options.custom', { defaultValue: 'Custom…' }),
      value: 'custom',
    };
    // Custom needs a reference date to compute defaults (day-of-month, ordinal weekday, min end
    // date), so hide it until a date is set.
    if (!startDate || Number.isNaN(startDate.getTime())) {
      return [oneTimeOption];
    }

    const weekday = format(startDate, 'EEEE', { locale: dateLocale });
    const ordinal = ordinalLabel(t, getOrdinalWeekdayCode(startDate));
    const monthDay = format(startDate, 'MMMM d', { locale: dateLocale });

    return [
      oneTimeOption,
      { label: t('Options.daily', { defaultValue: 'Daily' }), value: 'daily' },
      { label: t('Options.weekly', { defaultValue: `Weekly on ${weekday}`, weekday }), value: 'weekly' },
      {
        label: t('Options.monthly', { defaultValue: `Monthly on the ${ordinal} ${weekday}`, ordinal, weekday }),
        value: 'monthly',
      },
      { label: t('Options.annually', { defaultValue: `Annually on ${monthDay}`, monthDay }), value: 'annually' },
      { label: t('Options.semiMonthly', { defaultValue: 'Semi-Monthly' }), value: 'semi-monthly' },
      { label: t('Options.weekdays', { defaultValue: 'Every weekday (Monday to Friday)' }), value: 'weekdays' },
      customOption,
    ];
  }

  function selectCustom() {
    onChange({
      preset: 'custom',
      interval: value.interval || 1,
      unit: value.unit || 'week',
      weekdays: value.weekdays || [],
      monthlyRepeatOn: value.monthlyRepeatOn || 'dayOfMonth',
    });
    if (!ends) onEndsChange?.({ mode: 'occurrences', count: '' });
  }

  return (
    <Select
      version="2"
      name={name || 'recurrence'}
      value={value.preset}
      disabled={disabled}
      placeholder={t('placeholder', { defaultValue: 'Select Recurrence' })}
      options={presetOptions()}
      sort={false}
      renderSelectedValue={(data, { label, Placeholder }) => (data ? label : <Placeholder />)}
      renderOption={(o, { Option, SelectedIcon, selected, preventDefault, close }) => (
        <Option
          onClick={() => {
            // Drive selection ourselves — the Select's own handling only fires on value change,
            // so re-clicking an already-selected option would otherwise do nothing.
            preventDefault();
            close();
            if (o.value === 'custom') {
              selectCustom();
              return;
            }
            onChange({ preset: o.value as RecurrencePreset });
          }}
        >
          {o.label}
          {selected && <SelectedIcon />}
        </Option>
      )}
    />
  );
}
